import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Button, Dialog, DialogContent, Typography, CircularProgress } from '@mui/material';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Plus } from 'lucide-react';
import styles from './GameComposer.module.scss';
import CategoryItem, { GameCategory } from './CategoryItem';
import CreateGameCategory from './CreateGameCategory';
import CategoryGames from './CategoryGames';
import FirstPageGames from './FirstPageGames';
import ConfirmDeleteModal from 'components/modals/ConfirmDeleteModal';
import ImageUploader from 'components/imageUploader/imageUploader';
import { gameCategoryService } from 'components/api/gameCategories';
import { useAppDispatch } from 'store/hooks';
import { showToast } from 'store/toastSlice';

const GameComposer: React.FC = () => {
  const dispatch = useAppDispatch();
  const { id: siteId } = useParams<{ id: string }>();
  const [categories, setCategories] = useState<GameCategory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<GameCategory | null>(null);
  const [deleteModal, setDeleteModal] = useState<{
    open: boolean;
    id: number | string;
    title: string;
  }>({
    open: false,
    id: '',
    title: '',
  });
  const [hasChanges, setHasChanges] = useState(false);
  const [selectedCategoryForGames, setSelectedCategoryForGames] = useState<GameCategory | null>(
    null,
  );
  const [selectedCategoryForFirstPageGames, setSelectedCategoryForFirstPageGames] =
    useState<GameCategory | null>(null);
  const [imageModal, setImageModal] = useState<{ open: boolean; category: GameCategory | null }>({
    open: false,
    category: null,
  });
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  const fetchCategories = async () => {
    try {
      setIsLoading(true);
      const data = await gameCategoryService.getCategories(siteId);
      const list = data?.data?.docs || [];
      const formattedList = list.map((cat: any) => ({
        id: cat._id || cat.id,
        title: cat.title,
        translations: cat.translations || {},
        gameCount: cat.gamesCount || 0,
        showOnFirstPage: cat.firstPageEnabled || false,
        image: cat.image,
      }));
      setCategories(formattedList);
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, [siteId]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );

  const handleSaveCategory = async (category: GameCategory) => {
    try {
      if (editingCategory) {
        await gameCategoryService.updateCategory(category.id, {
          title: category.title,
          translations: category.translations || {},
        });
        setCategories(
          categories.map((cat: GameCategory) =>
            cat.id === category.id
              ? { ...cat, title: category.title, translations: category.translations || {} }
              : cat,
          ),
        );
        dispatch(showToast({ text: 'Category updated successfully!', type: 'success' }));
      } else {
        const res = await gameCategoryService.createCategory({
          title: category.title,
          siteId: Number(siteId),
          ...(category.translations ? { translations: category.translations } : {}),
        });
        const newCat = res?.data || res;
        const formatted = {
          id: newCat?._id || newCat?.id || Date.now(),
          title: newCat?.title || category.title,
          translations: newCat?.translations || category.translations || {},
          gameCount: newCat?.gameCount || 0,
          showOnFirstPage: newCat?.showOnFirstPage || false,
          image: newCat?.image,
        };
        setCategories([...categories, formatted]);
        dispatch(showToast({ text: 'Category created successfully!', type: 'success' }));
      }
      setIsModalOpen(false);
      setEditingCategory(null);
    } catch (error) {
      console.error('Error saving category:', error);
      dispatch(showToast({ text: 'Failed to save category', type: 'error' }));
    }
  };

  const handleSaveOrder = async () => {
    const ids = categories.map((cat) => cat.id);
    try {
      await gameCategoryService.reorderCategories(ids);
      setHasChanges(false);
      dispatch(showToast({ text: 'Category order saved successfully!', type: 'success' }));
    } catch (error) {
      console.error('Error saving category order:', error);
      dispatch(showToast({ text: 'Failed to save category order', type: 'error' }));
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setCategories((items) => {
        const oldIndex = items.findIndex((i) => i.id === active.id);
        const newIndex = items.findIndex((i) => i.id === over.id);
        const newOrder = arrayMove(items, oldIndex, newIndex);
        setHasChanges(true);
        return newOrder;
      });
    }
  };

  const handleDelete = (id: number | string) => {
    const category = categories.find((cat) => cat.id === id);
    if (category) {
      setDeleteModal({ open: true, id, title: category.title });
    }
  };

  const confirmDelete = async () => {
    try {
      await gameCategoryService.deleteCategory(deleteModal.id);
      setCategories(categories.filter((cat: GameCategory) => cat.id !== deleteModal.id));
      dispatch(showToast({ text: 'Category deleted successfully!', type: 'success' }));
    } catch (error) {
      console.error('Error deleting category:', error);
      dispatch(showToast({ text: 'Failed to delete category', type: 'error' }));
    } finally {
      setDeleteModal({ ...deleteModal, open: false });
    }
  };

  const handleEdit = (category: GameCategory) => {
    setEditingCategory(category);
    setIsModalOpen(true);
  };

  const handleManageGames = (category: GameCategory) => {
    setSelectedCategoryForGames(category);
  };

  const handleManageFirstPageGames = (category: GameCategory) => {
    setSelectedCategoryForFirstPageGames(category);
  };

  const handleManageImage = (category: GameCategory) => {
    setImageModal({ open: true, category });
    setSelectedImage(category.image || null);
  };

  const handleUpdateImage = () => {
    if (imageModal.category) {
      setCategories(
        categories.map((cat) =>
          cat.id === imageModal.category!.id ? { ...cat, image: selectedImage || undefined } : cat,
        ),
      );
      setHasChanges(true);
      dispatch(showToast({ text: 'Category image updated successfully!', type: 'success' }));
    }
    setImageModal({ open: false, category: null });
    setSelectedImage(null);
  };

  const handleToggleShowOnFirstPage = async (catId: number | string) => {
    const category = categories.find((cat) => cat.id === catId);
    if (!category) return;

    const newStatus = !category.showOnFirstPage;
    setCategories((prev) =>
      prev.map((cat) => (cat.id === catId ? { ...cat, showOnFirstPage: newStatus } : cat)),
    );

    try {
      await gameCategoryService.toggleCategoryShow(catId, newStatus);
      dispatch(showToast({ text: 'Category visibility updated successfully!', type: 'success' }));
    } catch (error) {
      console.error('Error toggling category status:', error);
      dispatch(showToast({ text: 'Failed to update category visibility', type: 'error' }));
      // Revert on error
      setCategories((prev) =>
        prev.map((cat) => (cat.id === catId ? { ...cat, showOnFirstPage: !newStatus } : cat)),
      );
    }
  };

  return (
    <Box className={styles.container}>
      {selectedCategoryForGames ? (
        <CategoryGames
          category={selectedCategoryForGames}
          onBack={() => {
            setSelectedCategoryForGames(null);
            fetchCategories();
          }}
        />
      ) : selectedCategoryForFirstPageGames ? (
        <FirstPageGames
          category={selectedCategoryForFirstPageGames}
          onBack={() => setSelectedCategoryForFirstPageGames(null)}
        />
      ) : (
        <>
          <Box className={styles.header}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              {hasChanges && (
                <Button variant="contained" className={styles.saveBtn} onClick={handleSaveOrder}>
                  Save Order
                </Button>
              )}
              <Button
                variant="contained"
                startIcon={<Plus size={18} />}
                className={styles.addBtn}
                onClick={() => setIsModalOpen(true)}
              >
                Add Category
              </Button>
            </Box>
          </Box>

          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress sx={{ color: 'var(--accent-blue)' }} />
            </Box>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext items={categories} strategy={verticalListSortingStrategy}>
                <Box className={styles.categoryList}>
                  {categories.map((cat) => (
                    <CategoryItem
                      key={cat.id}
                      category={cat}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                      onManageGames={handleManageGames}
                      onToggleShowOnFirstPage={handleToggleShowOnFirstPage}
                      onManageFirstPageGames={handleManageFirstPageGames}
                      onManageImage={handleManageImage}
                    />
                  ))}
                </Box>
              </SortableContext>
            </DndContext>
          )}
        </>
      )}

      <Dialog
        open={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingCategory(null);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          style: {
            backgroundColor: 'var(--bg-header)',
            borderRadius: '16px',
            border: '1px solid var(--border-color)',
          },
        }}
        disableRestoreFocus
      >
        <DialogContent sx={{ p: 0 }}>
          <CreateGameCategory
            onClose={() => {
              setIsModalOpen(false);
              setEditingCategory(null);
            }}
            onSuccess={handleSaveCategory}
            initialData={editingCategory}
            siteId={siteId}
          />
        </DialogContent>
      </Dialog>

      <ConfirmDeleteModal
        open={deleteModal.open}
        title={deleteModal.title}
        onClose={() => setDeleteModal({ ...deleteModal, open: false })}
        onConfirm={confirmDelete}
      />

      <Dialog
        open={imageModal.open}
        onClose={() => {
          setImageModal({ open: false, category: null });
          setSelectedImage(null);
        }}
        PaperProps={{
          sx: {
            bgcolor: 'var(--bg-panel)',
            backgroundImage: 'none',
            minWidth: '400px',
            border: '1px solid var(--border-color)',
          },
        }}
      >
        <DialogContent sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="h6" sx={{ color: 'white', mb: 1 }}>
            Update Category Image
          </Typography>
          <ImageUploader
            initialPreview={selectedImage}
            onImageSelect={(_, base64) => setSelectedImage(base64)}
          />
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
            <Button
              sx={{ color: 'white' }}
              onClick={() => {
                setImageModal({ open: false, category: null });
                setSelectedImage(null);
              }}
            >
              Cancel
            </Button>
            <Button variant="contained" onClick={handleUpdateImage}>
              Save
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default GameComposer;
